// src/App.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './App.css';
import { planCarreraAbogacia } from './data/abogaciaUbaData.js';
import CareerMapWrapper from './components/CareerMapWrapper';
import ConfigPanel from './components/ConfigPanel';
import CPOPanel from './components/CPOPanel';
import HelpGuide from './components/HelpGuide';
import Header from './components/Header';
import TopBar from './components/TopBar';
import { CPOProvider } from './contexts/CPOContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider, useToast } from './contexts/ToastContext';
import StatusToast from './components/StatusToast';
import ChangeHistoryPanel from './components/ChangeHistoryPanel';
import useMateriaFeedback from './hooks/useMateriaFeedback';
import { BrowserRouter } from 'react-router-dom';
import NavBar from './components/NavBar';
import MateriasLista from './components/MateriasLista';
// import AgendaEstudio from './components/AgendaEstudio';
import { useMaterias } from './hooks/useMaterias';
import { useOrientacion } from './hooks/useOrientacion';
import {
  calcularCursadasAprobadasCPC,
  obtenerPrerrequisitosRecursivos,
  obtenerMateriasHabilitadasPor
} from './utils/careerLogic';

// Future flags configuration for React Router v7 compatibility
const routerFuture = {
  v7_startTransition: true,
  v7_relativeSplatPath: true
};

// Componente interno que usa el contexto de toast
function AppContentWithToast() {
  const { showSuccess, showError, showInfo } = useToast();

  // --- ESTADOS PRINCIPALES ---
  const [mostrarCPOdetallado, setMostrarCPOdetallado] = useState(false);
  const [mostrarCPOpanel, setMostrarCPOpanel] = useState(false);
  const [hasConfirmedRequirements, setHasConfirmedRequirements] = useState(() => {
    return localStorage.getItem('hasConfirmedRequirements') === 'true';
  });
  const [showHelpGuide, setShowHelpGuide] = useState(() => {
    // Mostrar tutorial automáticamente en primera visita
    const hasVisitedBefore = localStorage.getItem('hasVisitedBefore');
    const hasSavedProgress = localStorage.getItem('materias');
    
    // Mostrar tutorial si es primera visita O si no hay progreso guardado
    return !hasVisitedBefore || !hasSavedProgress;
  });
  const [openFeedbackTab, setOpenFeedbackTab] = useState(false);
  const [materiaSeleccionadaParaExplorar, setMateriaSeleccionadaParaExplorar] = useState(null);
  const [prerrequisitosExplorar, setPrerrequisitosExplorar] = useState(new Set());
  const [habilitadasExplorar, setHabilitadasExplorar] = useState(new Set());
  const [activeTab, setActiveTab] = useState('mapa');
  const [cbcExpanded, setCbcExpanded] = useState(() => {
    const saved = localStorage.getItem('cbcExpanded');
    return saved ? JSON.parse(saved) : false; // Por defecto colapsado
  });
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const { summary, recentlyChangedIds, history, notifyChange, clearSummary } = useMateriaFeedback();
  
  // --- CUSTOM HOOKS ---
  const { orientacionSeleccionada, setOrientacionSeleccionada, resetOrientacion } = useOrientacion();
  
  // --- ESTADOS DERIVADOS / CALCULADOS ---
  const { materias, setMaterias, reiniciarProgreso, updateMateriaEstado, updateNotaFecha, cbcAprobado, aprobarCBC, cpoPuntos: cpoPuntosGenericos } = useMaterias(
    planCarreraAbogacia,
    orientacionSeleccionada ? orientacionSeleccionada.id : ''
  );
  
  const cpcCursadasCount = useMemo(() => calcularCursadasAprobadasCPC(materias), [materias]);
  // Eliminado: finales obsoletos; no se usa en UI
  // CPO context se consumirá dentro de los componentes hijos (TopBar/ConfigPanel/etc.)
  
  const listaOrientaciones = useMemo(() => {
    if (!planCarreraAbogacia.orientaciones) return [];
    return Object.entries(planCarreraAbogacia.orientaciones).map(([key, orientacion]) => ({
      id: key,
      ...orientacion
    }));
  }, []);

  // --- EFECTOS ---
  useEffect(() => {
    localStorage.setItem('cbcExpanded', JSON.stringify(cbcExpanded));
  }, [cbcExpanded]);



  // --- HANDLERS ---
  const handleMateriaChangeStatus = useCallback((materiaId, nuevoEstado) => {
    const materiasRecalculadas = updateMateriaEstado(materiaId, nuevoEstado);
    const materia = materiasRecalculadas[materiaId];
    if (materia) {
      notifyChange({ id: materia.id, nombre: materia.nombre }, nuevoEstado);
    }
  }, [updateMateriaEstado, notifyChange]);

  const handleNotaChange = useCallback((materiaId, nota, fecha = null) => {
    updateNotaFecha(materiaId, nota, fecha);
  }, [updateNotaFecha]);

  const handleExplorarMateria = useCallback((materiaId) => {
    if (!materiaId) {
      setMateriaSeleccionadaParaExplorar(null);
      setPrerrequisitosExplorar(new Set());
      setHabilitadasExplorar(new Set());
      return;
    }

    setMateriaSeleccionadaParaExplorar(materiaId);
    
    // Calcular prerrequisitos y materias habilitadas
    const prerrequisitos = obtenerPrerrequisitosRecursivos(materiaId, planCarreraAbogacia, materias);
    const habilitadas = obtenerMateriasHabilitadasPor(materiaId, planCarreraAbogacia, materias);
    
    setPrerrequisitosExplorar(new Set(prerrequisitos));
    setHabilitadasExplorar(new Set(habilitadas));
  }, [materias]);

  const handleConfirmRequirements = useCallback(() => {
    setHasConfirmedRequirements(true);
    localStorage.setItem('hasConfirmedRequirements', 'true');
    localStorage.setItem('hasVisitedBefore', 'true');
    showSuccess('¡Bienvenido al mapa de Abogacía UBA! 🎓');
  }, [showSuccess]);

  const handleAprobarTodasCBC = useCallback(() => {
    aprobarCBC();
    showSuccess('CBC aprobado - Se habilitan correlatividades del CPC');
  }, [aprobarCBC, showSuccess]);

  const handleToggleCbcExpanded = useCallback(() => {
    setCbcExpanded(prev => {
      const newExpanded = !prev;
      showInfo(newExpanded ? 'CBC expandido - Ver materias individuales' : 'CBC colapsado - Vista simplificada');
      return newExpanded;
    });
  }, [showInfo]);

  const handleReiniciarProgreso = useCallback(() => {
    reiniciarProgreso();
    resetOrientacion();
    setMateriaSeleccionadaParaExplorar(null);
    setPrerrequisitosExplorar(new Set());
    setHabilitadasExplorar(new Set());
    setCbcExpanded(false);
    localStorage.removeItem('cbcExpanded');
    try {
      // Limpiar estado CPO contextual persistido para evitar inconsistencias tras reset
      localStorage.removeItem('cpo:v1');
      localStorage.removeItem('cpo:orientation');
    } catch (_) { /* no-op */ }
    showInfo('Progreso reiniciado - Comenzando desde cero 🔄');
  }, [reiniciarProgreso, resetOrientacion, showInfo]);

  const handlePuntosChange = useCallback((nuevosPuntos) => {
    // Implementar lógica de cambio de puntos si es necesario
  }, []);

  // Eliminado: openFeedbackPanel no utilizado

  // Función para importar datos
  const handleImportData = useCallback((importedData) => {
    try {
      if (importedData.materias) {
        setMaterias(importedData.materias);
        showSuccess('✅ Datos importados exitosamente');
      }
      if (importedData.orientacionSeleccionada) {
        setOrientacionSeleccionada(importedData.orientacionSeleccionada);
      }
      if (importedData.cbcExpanded !== undefined) {
        setCbcExpanded(importedData.cbcExpanded);
      }
    } catch (error) {
      showError('❌ Error al importar datos');
      console.error('Error importing data:', error);
    }
  }, [setMaterias, setOrientacionSeleccionada, showSuccess, showError]);

  // Función para renderizar el contenido de las pestañas
  const renderTabContent = () => {
    switch (activeTab) {
      case 'mapa':
        return (
          <div className="tab-content active" style={{ 
            minHeight: '600px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <CareerMapWrapper
              plan={planCarreraAbogacia}
              materias={materias}
              onMateriaChangeStatus={handleMateriaChangeStatus}
              onNotaChange={handleNotaChange}
              cbcAprobado={cbcAprobado}
              recentlyChangedIds={recentlyChangedIds}
              orientacionSeleccionada={orientacionSeleccionada}
              onSelectOrientacion={(value) => {
                // Acepta objeto completo o id string
                if (typeof value === 'string') {
                  const found = listaOrientaciones.find(o => o.id === value) || null;
                  setOrientacionSeleccionada(found);
                } else {
                  setOrientacionSeleccionada(value);
                }
              }}
              listaOrientaciones={listaOrientaciones}
              cpcAprobadasCount={cpcCursadasCount}
              mostrarCPOdetallado={mostrarCPOdetallado}
              toggleMostrarCPOdetallado={() => setMostrarCPOdetallado(!mostrarCPOdetallado)}
              materiaSeleccionadaParaExplorar={materiaSeleccionadaParaExplorar}
              onExplorarMateria={handleExplorarMateria}
              prerrequisitosExplorar={prerrequisitosExplorar}
              habilitadasExplorar={habilitadasExplorar}
              
              onPuntosChange={handlePuntosChange}
              hasConfirmedRequirements={hasConfirmedRequirements}
              onConfirmRequirements={handleConfirmRequirements}
              onAprobarTodasCBC={handleAprobarTodasCBC}
              cbcExpanded={cbcExpanded}
              onToggleCbcExpanded={handleToggleCbcExpanded}
            />
          </div>
        );
      case 'materias':
        return (
          <MateriasLista
            materias={materias}
            onNotaChange={handleNotaChange}
            onMateriaChangeStatus={handleMateriaChangeStatus}
            mode="table"
          />
        );
      case 'agenda':
        return (
          <div className="tab-content active" style={{ 
            minHeight: '600px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto'
          }}>
            {/* Nueva Agenda */}
            {(() => {
              const AgendaPage = require('./agenda/components/AgendaPage.jsx').default;
              return <AgendaPage />;
            })()}
          </div>
        );
      default:
        return (
          <div className="tab-content active" style={{ 
            minHeight: '600px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flex: 1,
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: '3em', marginBottom: '20px' }}>📚</div>
              <h3>Selecciona una pestaña para comenzar</h3>
            </div>
          </div>
        );
    }
  };

  return (
    <CPOProvider materias={materias} orientacionFromApp={orientacionSeleccionada}>
    <div className="App">
      {/* Header + TopBar */}
      <Header onShowHelpGuide={() => setShowHelpGuide(true)} />
      <TopBar onReset={handleReiniciarProgreso} />

      {/* NavBar siempre visible */}
      <NavBar activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Contenido principal */}
      <main style={{ 
        flex: 1, 
        padding: '20px', 
        maxWidth: '1400px', 
        margin: '0 auto',
        minHeight: '600px', // Altura mínima explícita
        paddingBottom: '100px', // Espacio para el footer
        display: 'flex',
        flexDirection: 'column',
        // Asegurar que las dimensiones se mantengan siempre
        flexShrink: 0,
        overflow: 'hidden' // Evitar scroll innecesario
      }}>
        {/* Contenedor específico para el contenido de tabs */}
        <div className="tab-content-container" style={{
          flex: 1,
          minHeight: '600px',
          display: 'flex',
          flexDirection: 'column',
          // Asegurar que las dimensiones se mantengan siempre
          flexShrink: 0,
          overflow: 'hidden' // Evitar scroll innecesario
        }}>
          {renderTabContent()}
        </div>
      </main>

      {/* Footer siempre visible */}
      <footer className="App-footer">
        <div className="credits">
          <div>
            <p>© 2024 Mapa de Abogacía UBA - Desarrollado por Sebastian Soto con ❤️ para estudiantes</p>
            <p style={{ fontSize: '0.8em', marginTop: '5px', color: 'var(--text-secondary)' }}>
              Inspirado en la app de Fede del Mazo - Gracias por la inspiración! 🎓
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <a 
              href="https://www.linkedin.com/in/sebastian-soto-76289a23b/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="linkedin-button"
            >
              💼 LinkedIn
            </a>
            <a 
              href="mailto:soto127@est.derecho.uba.ar" 
              className="contact-button"
            >
              📧 Contacto
            </a>
          </div>
        </div>
      </footer>

      {/* Paneles flotantes siempre disponibles */}
      
      {/* Botones flotantes para acceder a paneles */}
      <div style={{
        position: 'fixed',
        right: '20px',
        bottom: '120px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        zIndex: 1000
      }}>
        {/* Botón CBC aprobado */}
        <button
          onClick={() => !cbcAprobado ? handleAprobarTodasCBC() : null}
          disabled={cbcAprobado}
          style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            backgroundColor: cbcAprobado ? 'var(--bg-secondary)' : 'var(--warning-color)',
            color: cbcAprobado ? 'var(--text-secondary)' : 'var(--text-inverse)',
            border: 'none',
            cursor: cbcAprobado ? 'default' : 'pointer',
            fontSize: '0.8em',
            boxShadow: 'var(--shadow-medium)',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title={cbcAprobado ? 'CBC aprobado ✓' : 'Marcar CBC aprobado'}
        >
          {cbcAprobado ? 'CBC ✓' : 'CBC'}
        </button>

        {/* Botón para panel CPO */}
        <button
          onClick={() => setMostrarCPOpanel(true)}
          style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            backgroundColor: 'var(--accent-color)',
            color: 'var(--text-inverse)',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1.2em',
            boxShadow: 'var(--shadow-medium)',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = 'var(--accent-hover)';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = 'var(--accent-color)';
            e.target.style.transform = 'scale(1)';
          }}
          title="Panel CPO - Orientaciones"
        >
          🎯
        </button>

        {/* Botón para panel de configuración */}
        <button
          onClick={() => setShowConfigPanel(true)}
          style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            backgroundColor: 'var(--success-color)',
            color: 'var(--text-inverse)',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1.2em',
            boxShadow: 'var(--shadow-medium)',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = 'var(--success-color)';
            e.target.style.transform = 'scale(1.1)';
            e.target.style.opacity = '0.9';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = 'var(--success-color)';
            e.target.style.transform = 'scale(1)';
            e.target.style.opacity = '1';
          }}
          title="Panel de Configuración"
        >
          ⚙️
        </button>
      </div>
      
      {/* Panel de Ayuda */}
      {showHelpGuide && (
        <HelpGuide
          onClose={() => setShowHelpGuide(false)}
          isOpen={showHelpGuide}
        />
      )}

      {/* Panel CPO */}
      {mostrarCPOpanel && (
        <CPOPanel
          onClose={() => setMostrarCPOpanel(false)}
          orientacionSeleccionada={orientacionSeleccionada}
          onSelectOrientacion={setOrientacionSeleccionada}
          listaOrientaciones={listaOrientaciones}
          onPuntosChange={handlePuntosChange}
          materias={materias}
          updateMateriaEstado={updateMateriaEstado}
          updateNotaFecha={updateNotaFecha}
        />
      )}

      {/* Panel de Configuración */}
      {showConfigPanel && (
          <ConfigPanel
          materias={materias}
          orientacionSeleccionada={orientacionSeleccionada}
          cpoPuntos={cpoPuntosGenericos}
          onImportData={handleImportData}
          mostrarCPOdetallado={mostrarCPOdetallado}
          toggleMostrarCPOdetallado={() => setShowConfigPanel(false)}
          openFeedbackTab={openFeedbackTab}
          setOpenFeedbackTab={setOpenFeedbackTab}
        />
      )}

      {/* Status toast resumido */}
      <StatusToast summary={summary} onClose={clearSummary} />

      {/* Panel de historial (opcional) */}
      <ChangeHistoryPanel history={history} />

      {/* Panel de Feedback */}
      {openFeedbackTab && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'var(--bg-panel)',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: 'var(--shadow-large)'
          }}>
            <button
              onClick={() => setOpenFeedbackTab(false)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'none',
                border: 'none',
                fontSize: '1.5em',
                cursor: 'pointer',
                color: 'var(--text-secondary)'
              }}
            >
              ×
            </button>
            <h2 style={{ marginBottom: '20px', color: 'var(--accent-color)' }}>
              📝 Enviar Feedback
            </h2>
            <p style={{ marginBottom: '20px', lineHeight: '1.6' }}>
              ¿Encontraste algún error o tenés alguna sugerencia? ¡Nos encantaría escucharte!
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <a
                href="mailto:feedback@mapa-abogacia-uba.com"
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'var(--accent-color)',
                  color: 'var(--text-inverse)',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = 'var(--accent-hover)'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'var(--accent-color)'}
              >
                📧 Enviar Email
              </a>
              <button
                onClick={() => setOpenFeedbackTab(false)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = 'var(--bg-hover)'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'var(--bg-secondary)'}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </CPOProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <BrowserRouter
          future={routerFuture}
        >
          <AppContentWithToast />
        </BrowserRouter>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;